import type { B2BCart, B2BCartItem, B2BCartResponse } from '~/types'

/**
 * The B2B cart.
 *
 * Deliberately separate from the inherited `useCartStore`, which is the B2C
 * checkout machine: a cart-hash cookie, vouchers, credits, deals, delivery and
 * payment steps, none of which the B2B backend serves. Here the cart belongs to
 * the signed-in customer, a line is addressed by its purchase unit
 * (`itemId` + `minQty`) rather than by the SKU, and every mutation answers with
 * the whole cart — so there is no fetch-after-write anywhere below.
 *
 * There is no anonymous cart: without a customer the endpoints 401, so every
 * action short-circuits when nobody is signed in rather than letting a listing
 * page fire a doomed request per click.
 */
export const useB2BCartStore = defineStore('b2bCart', () => {
  const cart = ref<B2BCart | null>(null)

  /**
   * Requests in flight, not a boolean. Writes are not queued here — the backend
   * serialises them per customer — so two can overlap, and with a single flag
   * whichever finished first cleared it while the other was still running.
   */
  const pending = ref(0)
  const loading = computed(() => pending.value > 0)

  /**
   * In-flight requests that are not about one line (`fetchCart`, `clearCart`,
   * the PDF). Only these put the checkout overlay over the page: a line edit
   * is scoped to its own row, and blocking the whole list for it made every
   * other trash button and stepper dead until the response landed — three
   * quick removals removed one line.
   */
  const blockingPending = ref(0)
  const blocking = computed(() => blockingPending.value > 0)

  /** `variantId`s with a write in flight, so a row can disable only itself. */
  const busyLines = ref<string[]>([])
  const error = ref<string | null>(null)
  /**
   * The backend's own wording for the last write.
   *
   * `cart/add|update|remove` put either their success text or a stock clamp
   * ("Apgailestaujame, tačiau šiuo metu galite įsigyti tik N vnt. …") in the
   * same `message` field — the warning simply outranks the success text
   * server-side. Kept rather than dropped because the clamp is the only place
   * the caller learns its requested amount was not the amount stored.
   */
  const message = ref<string | null>(null)

  /**
   * Whether that `message` is the clamp rather than the success text.
   *
   * The two used to be indistinguishable, so a refused add — the line is
   * already at the SKU's stock — still opened the "Prekė įdėta į krepšelį"
   * modal while the cart did not move. The backend now flags it
   * (`data.warning`), and both the product page and the catalogue warn instead
   * of confirming. A backend without the field reads as `false`, which is the
   * old behaviour rather than a break.
   */
  const warning = ref(false)

  const items = computed<B2BCartItem[]>(() => cart.value?.items ?? [])
  /** Pieces, not lines — `Carts::count_items()` on the backend multiplies by `min_qty`. */
  const itemCount = computed(() => cart.value?.itemCount ?? 0)
  const sum = computed(() => Number(cart.value?.sum ?? 0))

  /**
   * The offer this cart is pinned to, or null for an ordinary cart.
   *
   * While it is set the backend refuses any SKU outside that offer, so the
   * catalogue and the cart page both have to say so instead of letting every
   * add-to-cart click fail on its own.
   */
  const offerId = computed(() => cart.value?.offerId ?? null)
  const offerNumber = computed(() => cart.value?.offerNumber ?? null)

  // Every catalogue variant row asks `lineFor` after each cart change, so a scan
  // of the lines per row made a page of rows cost rows x lines.
  const linesByVariant = computed(() => new Map(items.value.map(item => [item.variantId, item])))

  /** The line a given catalogue variant is held in, or null when it is not in the cart. */
  function lineFor(variantId: string): B2BCartItem | null {
    return linesByVariant.value.get(variantId) ?? null
  }

  function isBusy(variantId: string) {
    return busyLines.value.includes(variantId)
  }

  /** The backend's own wording out of a thrown ofetch error, when there is one. */
  function apiMessage(e: unknown): string | null {
    const body = (e as { data?: unknown, response?: { _data?: unknown } })?.data
      ?? (e as { response?: { _data?: unknown } })?.response?._data
    const message = (body as { message?: unknown })?.message
    return typeof message === 'string' ? message : null
  }

  // Every response carries the whole cart, so the newest one is the truth. Two
  // overlapping writes can answer out of order, and applying the older one
  // last would roll the newer write back on screen until the next request.
  let issued = 0
  let applied = 0

  function apply(data: B2BCartResponse, seq = ++issued) {
    if (seq > applied) {
      applied = seq
      cart.value = data.cart
    }
    message.value = data.message ?? null
    warning.value = data.warning ?? false
    return data
  }

  /**
   * Runs an API call with the busy flag and the error slot managed, and returns
   * null instead of throwing: every caller is a button, and a failed add must
   * leave the listing usable rather than bubble into an error page.
   */
  async function run(
    fn: () => Promise<{ success: boolean, data: B2BCartResponse, message?: string }>,
    line: string | null = null,
  ) {
    const seq = ++issued
    pending.value++
    if (line) busyLines.value = [...busyLines.value, line]
    else blockingPending.value++
    error.value = null
    message.value = null
    warning.value = false
    try {
      const res = await fn()
      if (!res?.success) {
        error.value = res?.message ?? null
        return null
      }
      return apply(res.data, seq)
    }
    catch (e) {
      error.value = apiMessage(e)
      return null
    }
    finally {
      pending.value--
      if (line) {
        const i = busyLines.value.indexOf(line)
        busyLines.value = busyLines.value.filter((_, j) => j !== i)
      }
      else {
        blockingPending.value--
      }
    }
  }

  const lineKey = (itemId: string, minQty: number) => `${itemId}-${minQty}`

  const customerStore = useCustomerStore()

  async function fetchCart() {
    if (!customerStore.isLoggedIn) return null
    return run(() => useApi().getB2BCart())
  }

  /** Adds `amount` purchase units on top of what the line already holds. */
  async function addItem(itemId: string, minQty: number, amount: number) {
    if (!customerStore.isLoggedIn) return null
    return run(() => useApi().addB2BCartItem(itemId, minQty, amount), lineKey(itemId, minQty))
  }

  /** Sets the line to exactly `amount` purchase units; 0 removes it. */
  async function updateItem(itemId: string, minQty: number, amount: number) {
    if (!customerStore.isLoggedIn) return null
    return run(() => useApi().updateB2BCartItem(itemId, minQty, amount), lineKey(itemId, minQty))
  }

  async function removeItem(itemId: string, minQty: number) {
    if (!customerStore.isLoggedIn) return null
    return run(() => useApi().removeB2BCartItem(itemId, minQty), lineKey(itemId, minQty))
  }

  /**
   * Empties the cart in one call rather than a request per line, and with it
   * releases the offer scope — which is why the cart page offers it next to the
   * PDF/offer button rather than hiding it behind removing every line by hand.
   * The response carries the emptied cart, so there is nothing to re-fetch.
   */
  async function clearCart() {
    if (!customerStore.isLoggedIn) return null
    return run(() => useApi().clearB2BCart())
  }

  /**
   * Adopts a cart that arrived from an endpoint this store does not own — today
   * only `cart/add-many`, which the manager toolbar calls directly because it
   * takes a whole selection rather than one line. The response carries the
   * finished cart, so re-fetching it here would be a second round trip for a
   * payload already in hand.
   */
  /**
   * Saves the cart PDF to the visitor's downloads.
   *
   * The endpoint answers with bytes behind the Bearer token, so this cannot be a
   * link — the blob is fetched with the token and handed to an object URL. The
   * filename mirrors the backend's own Content-Disposition rather than being read
   * off it: `responseType: 'blob'` throws the headers away.
   */
  async function downloadPdf() {
    if (!customerStore.isLoggedIn) return false
    pending.value++
    blockingPending.value++
    error.value = null
    try {
      const blob = await useApi().getB2BCartPdf()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `krepselis-${cart.value?.id ?? ''}-${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      return true
    }
    catch (e) {
      error.value = apiMessage(e)
      return false
    }
    finally {
      pending.value--
      blockingPending.value--
    }
  }

  function setCart(next: B2BCart) {
    applied = ++issued
    cart.value = next
  }

  function reset() {
    applied = ++issued
    cart.value = null
    error.value = null
    message.value = null
    warning.value = false
  }

  return { cart, items, itemCount, sum, offerId, offerNumber, loading, blocking, error, message, warning, lineFor, isBusy, fetchCart, addItem, updateItem, removeItem, clearCart, downloadPdf, setCart, reset }
})
